import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface ReturnItem {
  item_id: string;
  item_code: string;
  item_name: string;
  quantity: number;
  unit_price: number;
  batch_number?: string;
  expiry_date?: string;
  po_item_id?: string;
  reason: string;
  reason_details?: string;
}

export interface VendorReturn {
  return_id: string;
  rma_number: string;
  vendor_id: string;
  vendor_name: string;
  purchase_order_id?: string;
  po_number?: string;
  status: string;
  reason: string;
  reason_details?: string;
  expected_credit: number;
  actual_credit?: number;
  credit_note_number?: string;
  requested_at: string;
  approved_at?: string;
  shipped_at?: string;
  received_at?: string;
  credited_at?: string;
  requested_by: string;
  tracking_number?: string;
  shipping_carrier?: string;
  notes?: string;
  items: ReturnItemResponse[];
  created_at: string;
}

export interface ReturnItemResponse {
  return_item_id: string;
  item_id: string;
  item_code: string;
  item_name: string;
  batch_number?: string;
  quantity_ordered: number;
  quantity_returned: number;
  quantity_received: number;
  unit_price: number;
  line_total: number;
  credit_amount?: number;
  reason: string;
}

export interface ReturnStats {
  by_status: Record<string, number>;
  total_pending: number;
  total_completed: number;
  top_vendors: Array<{ vendor_name: string; count: number }>;
}

export function useReturns() {
  const queryClient = useQueryClient();

  const getReturns = (params?: {
    status?: string;
    vendor_id?: string;
    page?: number;
    page_size?: number;
  }) =>
    useQuery<{ returns: VendorReturn[]; total: number; page: number; page_size: number }>({
      queryKey: ['returns', params],
      queryFn: () => api.get('/returns', { params }).then((res) => res.data),
    });

  const getReturn = (returnId: string) =>
    useQuery<VendorReturn>({
      queryKey: ['return', returnId],
      queryFn: () => api.get(`/returns/${returnId}`).then((res) => res.data),
      enabled: !!returnId,
    });

  const getStats = () =>
    useQuery<ReturnStats>({
      queryKey: ['return-stats'],
      queryFn: () => api.get('/returns/stats/summary').then((res) => res.data),
    });

  const createReturn = () =>
    useMutation({
      mutationFn: (data: {
        vendor_id: string;
        purchase_order_id?: string;
        original_invoice_id?: string;
        items: ReturnItem[];
        notes?: string;
        expected_credit?: number;
      }) => api.post('/returns', data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['returns'] });
        queryClient.invalidateQueries({ queryKey: ['return-stats'] });
      },
    });

  const approveReturn = () =>
    useMutation({
      mutationFn: (returnId: string) => api.post(`/returns/${returnId}/approve`),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['returns'] });
        queryClient.invalidateQueries({ queryKey: ['return-stats'] });
      },
    });

  const rejectReturn = () =>
    useMutation({
      mutationFn: ({ returnId, reason }: { returnId: string; reason: string }) =>
        api.post(`/returns/${returnId}/reject`, { reason }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['returns'] });
        queryClient.invalidateQueries({ queryKey: ['return-stats'] });
      },
    });

  const shipReturn = () =>
    useMutation({
      mutationFn: ({
        returnId,
        tracking_number,
        shipping_carrier,
        notes,
      }: {
        returnId: string;
        tracking_number: string;
        shipping_carrier: string;
        notes?: string;
      }) => api.post(`/returns/${returnId}/ship`, { tracking_number, shipping_carrier, notes }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['returns'] });
        queryClient.invalidateQueries({ queryKey: ['return-stats'] });
      },
    });

  const receiveReturn = () =>
    useMutation({
      mutationFn: ({
        returnId,
        items,
        notes,
      }: {
        returnId: string;
        items: Array<{ return_item_id: string; quantity_received: number; notes?: string }>;
        notes?: string;
      }) => api.post(`/returns/${returnId}/receive`, { items, notes }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['returns'] });
        queryClient.invalidateQueries({ queryKey: ['return-stats'] });
      },
    });

  const issueCredit = () =>
    useMutation({
      mutationFn: ({
        returnId,
        credit_note_number,
        actual_credit,
        notes,
      }: {
        returnId: string;
        credit_note_number: string;
        actual_credit: number;
        notes?: string;
      }) => api.post(`/returns/${returnId}/credit`, { credit_note_number, actual_credit, notes }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['returns'] });
        queryClient.invalidateQueries({ queryKey: ['return-stats'] });
      },
    });

  const cancelReturn = () =>
    useMutation({
      mutationFn: ({ returnId, reason }: { returnId: string; reason: string }) =>
        api.post(`/returns/${returnId}/cancel`, { reason }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['returns'] });
        queryClient.invalidateQueries({ queryKey: ['return-stats'] });
      },
    });

  return {
    getReturns,
    getReturn,
    getStats,
    createReturn,
    approveReturn,
    rejectReturn,
    shipReturn,
    receiveReturn,
    issueCredit,
    cancelReturn,
  };
}
